/**
 * Shared validation logic for pulse entry forms.
 * Extracted from PulseEntry.js to be reusable by both single-entry and bulk-entry modes.
 * All rules MUST match PulseEntry.js behavior exactly.
 */

/**
 * Check if a value is a valid non-negative integer.
 * Returns true for empty/null/undefined (treated as "not entered yet"),
 * or for valid non-negative integers.
 * @param {*} value - The value to check
 * @returns {boolean}
 */
export function isValidInteger(value) {
  if (value === '' || value === null || value === undefined) return true;
  const num = Number(value);
  return Number.isInteger(num) && num >= 0;
}

/**
 * Check if a row has no user-entered data.
 * Returns true if all non-metadata fields (those not starting with '_') are empty/zero/''.
 * @param {Object} rowData - The row form data
 * @returns {boolean}
 */
export function isRowEmpty(rowData) {
  if (!rowData) return true;
  return Object.entries(rowData).every(([key, val]) => {
    if (key.startsWith('_')) return true; // skip metadata
    return val === '' || val === null || val === undefined || val === 0 || val === '0';
  });
}

/**
 * Check if a row has at least one non-metadata numeric field with a non-empty, non-zero value.
 * @param {Object} rowData - The row form data
 * @returns {boolean}
 */
export function isRowFilled(rowData) {
  if (!rowData) return false;
  // Numeric fields that indicate user has started entering data
  const numericFields = [
    'dialed', 'dialed_company_ivr', 'dialed_fd_phone', 'connected',
    'visit_scheduled', 'visit_done', 'closure_expected', 'revenue_expected',
    'closure_phone', 'closure_counter'
  ];
  return numericFields.some(field => {
    const val = rowData[field];
    return val !== '' && val !== null && val !== undefined && val !== 0 && val !== '0';
  });
}

/**
 * Validate a single row of pulse entry data.
 * Returns a Record<field, errorMessage> object. Empty object means no errors.
 *
 * Rules (match PulseEntry.js exactly):
 * 1. Negative number check on numeric fields
 * 2. Integer-only check on all numeric fields (no decimals)
 * 3. Connected <= Dialed (when both valid)
 * 4. Dialed source split: dialed_company_ivr + dialed_fd_phone must equal dialed
 * 5. Visit Done <= Visit Scheduled (when both valid)
 * 6. Visit Scheduled required when source needs_visit === 'Yes' and source_id is set
 * 7. Revenue required when closure_expected > 0
 * 8. No revenue when closure_expected === 0
 * 9. closure_expected max 50
 *
 * @param {Object} rowData - The row form data
 * @param {boolean} sourceNeedsVisit - Whether the source requires visit scheduling
 * @returns {Object} Record<field, errorMessage>
 */
export function validateRow(rowData, sourceNeedsVisit = false) {
  const errors = {};
  if (!rowData) return errors;

  const dialed = parseInt(rowData.dialed) || 0;
  const connected = parseInt(rowData.connected) || 0;
  const scheduled = parseInt(rowData.visit_scheduled) || 0;
  const done = parseInt(rowData.visit_done) || 0;

  // --- Negative number validation ---
  const negativeCheckFields = [
    'dialed', 'connected', 'visit_scheduled', 'visit_done',
    'closure_expected', 'closure_phone', 'closure_counter'
  ];
  for (const field of negativeCheckFields) {
    if (rowData[field] && Number(rowData[field]) < 0) {
      errors[field] = 'Value cannot be negative';
    }
  }

  // closure_expected max 50
  if (rowData.closure_expected && Number(rowData.closure_expected) > 50) {
    errors.closure_expected = 'Maximum 50 closures allowed per pulse';
  }

  // --- Integer-only validation ---
  const integerCheckFields = [
    'dialed', 'connected', 'visit_scheduled', 'visit_done',
    'closure_expected', 'closure_phone', 'closure_counter'
  ];
  for (const field of integerCheckFields) {
    if (rowData[field] && !isValidInteger(rowData[field])) {
      errors[field] = 'Must be a whole number (no decimals)';
    }
  }

  // --- Connected <= Dialed (only if both are valid) ---
  if (rowData.connected && !errors.connected && !errors.dialed && connected > dialed) {
    errors.connected = 'Connected cannot exceed Dialed';
  }

  // --- Dialed calls source split validation ---
  const dialedCompanyIvr = parseInt(rowData.dialed_company_ivr) || 0;
  const dialedFdPhone = parseInt(rowData.dialed_fd_phone) || 0;
  const totalSourceCalls = dialedCompanyIvr + dialedFdPhone;

  if (rowData.dialed && !errors.dialed && totalSourceCalls !== dialed) {
    errors.dialed_company_ivr = `Company IVR + FD Phone must equal ${dialed} (currently ${totalSourceCalls})`;
    errors.dialed_fd_phone = `Company IVR + FD Phone must equal ${dialed} (currently ${totalSourceCalls})`;
  }

  // Validate individual source fields are integers
  if (rowData.dialed_company_ivr && !isValidInteger(rowData.dialed_company_ivr)) {
    errors.dialed_company_ivr = 'Must be a whole number (no decimals)';
  }
  if (rowData.dialed_fd_phone && !isValidInteger(rowData.dialed_fd_phone)) {
    errors.dialed_fd_phone = 'Must be a whole number (no decimals)';
  }

  // Validate non-negative for source fields
  if (rowData.dialed_company_ivr && Number(rowData.dialed_company_ivr) < 0) {
    errors.dialed_company_ivr = 'Value cannot be negative';
  }
  if (rowData.dialed_fd_phone && Number(rowData.dialed_fd_phone) < 0) {
    errors.dialed_fd_phone = 'Value cannot be negative';
  }

  // --- Visit Done <= Scheduled (only if both are valid) ---
  if (rowData.visit_done && !errors.visit_done && !errors.visit_scheduled && done > scheduled) {
    errors.visit_done = 'Visit Done cannot exceed Scheduled';
  }

  // --- Visit Scheduled required if source needs visit ---
  if (sourceNeedsVisit && !rowData.visit_scheduled) {
    errors.visit_scheduled = 'Required for this source type';
  }

  // --- Revenue validation ---
  const closureExpected = parseInt(rowData.closure_expected) || 0;
  const revenueExpected = parseFloat(rowData.revenue_expected) || 0;

  // Revenue mandatory when closure expected > 0
  if (closureExpected > 0 && revenueExpected <= 0) {
    errors.revenue_expected = 'Revenue is required when closure expected > 0';
  }

  // No revenue when no closures expected
  if (closureExpected === 0 && revenueExpected > 0) {
    errors.revenue_expected = 'Cannot have revenue without closure expected';
  }

  return errors;
}
